import { useState } from "react";
import Markdown from "react-markdown";

import { useQueryClient } from "@tanstack/react-query";

import useBatchGetAssets from "@/api/assets/useAssetsApi";
import { ArrowLeftCircleIcon, ArrowRightCircleIcon } from "@/assets/icons";
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious
} from "@/components/ui/carousel";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle
} from "@/components/ui/dialog";
import { GetAnalysisTemplateDetail } from "@/types/template";

import dayjs from "dayjs";
import { useDeepCompareEffect } from "use-deep-compare";

interface TemplateSummaryModalProps {
  readonly templateDetail: GetAnalysisTemplateDetail;
}

export default function TemplateSummaryModal({
  templateDetail
}: TemplateSummaryModalProps) {
  const queryClient = useQueryClient();
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(
    null
  );
  const [api, setApi] = useState<CarouselApi>();

  const { mutate: getAssets } = useBatchGetAssets({
    onSuccess: (data) => {
      queryClient.setQueryData(
        ["analysis-template-detail", templateDetail.id],
        (oldData: GetAnalysisTemplateDetail): GetAnalysisTemplateDetail => {
          return {
            ...oldData,
            screenshots: data.map((d) => ({
              expiry_at: d.expires_at,
              id: d.id,
              url: d.url
            }))
          };
        }
      );
    }
  });

  useDeepCompareEffect(() => {
    if (
      templateDetail.screenshots?.length > 0 &&
      templateDetail.screenshots[0].expiry_at &&
      dayjs().isBefore(templateDetail.screenshots[0].expiry_at)
    )
      return;
    const assetsId = templateDetail.screenshots?.map((item) => item.id);

    if (assetsId?.length > 0) {
      getAssets(assetsId);
    }
  }, [templateDetail.screenshots]);

  return (
    <>
      <div className="space-y-6">
        <div className="space-y-2">
          <p className="font-semibold text-black">Objective</p>
          <p className="text-dim-gray text-xs">{templateDetail.objective}</p>
        </div>

        <div className="space-y-2">
          <p className="font-semibold text-black">Problem Statement</p>
          <div className="text-dim-gray text-xs markdown">
            <Markdown>{templateDetail.problem_statement}</Markdown>
          </div>
        </div>

        <div className="space-y-2">
          <p className="font-semibold text-black">
            Sample Configuration Result
          </p>
          <Carousel>
            <CarouselContent className="-ml-4">
              {templateDetail.screenshots?.map((asset, index) => (
                <CarouselItem
                  key={asset.id}
                  className={`cursor-pointer basis-1/3 ${
                    index > 0 ? "pl-2" : ""
                  }`}
                  onClick={() => setSelectedImageIndex(index)}
                >
                  <img
                    src={asset.url}
                    alt={asset.id}
                    className="object-cover w-full h-full"
                  />
                </CarouselItem>
              ))}
            </CarouselContent>

            <CarouselPrevious variant="outline" />
            <CarouselNext variant="outline" />
          </Carousel>
        </div>
      </div>

      <Dialog
        open={selectedImageIndex !== null}
        onOpenChange={(isOpen) => !isOpen && setSelectedImageIndex(null)}
      >
        <DialogTitle className="sr-only">
          Sample Configuration Result
        </DialogTitle>
        <DialogDescription className="sr-only">
          Sample Configuration Result of the template
        </DialogDescription>
        <DialogContent
          role="dialog"
          aria-label="Sample Configuration Result"
          aria-describedby="sample-configuration-result"
          className="max-w-3xl bg-inherit border-none"
          isHideClose
        >
          {selectedImageIndex !== null && (
            <Carousel
              setApi={setApi}
              opts={{
                startIndex: selectedImageIndex,
                loop: true
              }}
            >
              <div className="relative">
                <CarouselContent>
                  {templateDetail.screenshots?.map((asset) => (
                    <CarouselItem key={asset.id}>
                      <img
                        src={asset.url}
                        alt={`Enlarged ${asset.id}`}
                        className="w-full h-full object-contain"
                      />
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <button
                  type="button"
                  aria-label="Previous image"
                  className="flex items-center justify-center bg-white size-9 rounded-full absolute -left-[10%] -translate-y-1/2 top-1/2"
                  onClick={() => api?.scrollPrev()}
                >
                  <ArrowLeftCircleIcon className="fill-dim-gray size-7.5" />
                </button>

                <button
                  type="button"
                  aria-label="Next image"
                  className="flex items-center justify-center bg-white size-9 rounded-full absolute -right-[10%] -translate-y-1/2 top-1/2"
                  onClick={() => api?.scrollNext()}
                >
                  <ArrowRightCircleIcon className="fill-dim-gray size-7.5 " />
                </button>
              </div>
            </Carousel>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
